import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { SiteContentProvider } from './context/SiteContentContext';
import ProtectedRoute from './routes/ProtectedRoute';
import DashboardLayout from './components/dashboard/DashboardLayout';
import Navbar from './components/shared/Navbar/Navbar';
import Footer from './components/shared/Footer/Footer';
import BookAConsult from './components/pages/Home/BookAConsult/BookAConsult';
import ScrollToTop from './components/shared/ScrollToTop/ScrollToTop';
import Loader from './components/shared/Loader/Loader';
import { usePageLoader } from './hooks/usePageLoader';
import { useScrollReveal } from './hooks/useScrollReveal';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const PagesManager = lazy(() => import('./features/cms/pages/PagesManager'));
const SectionsManager = lazy(() => import('./features/cms/sections/SectionsManager'));
const MediaManager = lazy(() => import('./features/cms/media/MediaManager'));
const ContactsManager = lazy(() => import('./features/contacts/ContactsManager'));
const ServicesManager = lazy(() => import('./features/services/ServicesManager'));
const ProjectsManager = lazy(() => import('./features/projects/ProjectsManager'));
const BlogsManager = lazy(() => import('./features/blogs/BlogsManager'));
const MenusManager = lazy(() => import('./features/menus/MenusManager'));
const DynamicContentPlaceholder = lazy(() =>
  import('./pages/admin/AdminRoutePlaceholders').then((module) => ({
    default: module.DynamicContentPlaceholder,
  }))
);
const SectionEditor = lazy(() => import('./features/cms/sections/SectionEditor'));
const LegacySectionEditorRedirect = lazy(() =>
  import('./features/cms/sections/LegacySectionEditorRedirect')
);
const Home = lazy(() => import('./pages/Home/Home'));
const About = lazy(() => import('./pages/About/About'));
const Service = lazy(() => import('./pages/Service/Service'));
const ServiceDetails = lazy(() => import('./pages/Service/ServiceDetails'));
const CoreModels = lazy(() => import('./pages/CoreModels/CoreModels'));
const Contact = lazy(() => import('./pages/Contact/Contact'));
const Collaborations = lazy(() => import('./pages/Collaborations/Collaborations'));
const Jobs = lazy(() => import('./pages/Jobs/Jobs'));
const Projects = lazy(() => import('./pages/Projects/Projects'));
const ProjectDetails = lazy(() => import('./pages/Projects/ProjectDetails'));
const BlogDetails = lazy(() => import('./pages/Blog/BlogDetails'));

function AppLayout() {
  const { pathname } = useLocation();
  const isReady = usePageLoader(pathname);
  const [showLoader, setShowLoader] = useState(true);
  const [loaderExiting, setLoaderExiting] = useState(false);
  const hasEnteredRef = useRef(false);
  const shellRef = useRef(null);
  useScrollReveal(shellRef, pathname);

  useEffect(() => {
    setShowLoader(true);
    setLoaderExiting(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = showLoader ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [showLoader]);

  useEffect(() => {
    if (!isReady) {
      return undefined;
    }

    setLoaderExiting(true);

    const timeoutId = window.setTimeout(() => {
      setShowLoader(false);
      hasEnteredRef.current = true;
    }, 450);

    return () => window.clearTimeout(timeoutId);
  }, [isReady]);

  const showApp = hasEnteredRef.current || isReady;

  return (
    <>
      {showLoader && <Loader isExiting={loaderExiting} />}
      <div
        ref={shellRef}
        className={`app-shell${showApp ? ' app-shell--ready' : ''}`}
      >
        <ScrollToTop />
        <Navbar />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Service />} />
            <Route path="/services/:id" element={<ServiceDetails />} />
            <Route path="/coremodels" element={<CoreModels />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/collaborations" element={<Collaborations />} />
            <Route path="/jobs" element={<Jobs />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/:id" element={<ProjectDetails />} />
            <Route path="/blog/:slug" element={<BlogDetails />} />
          </Routes>
        </Suspense>
        {pathname !== '/contact' ? <BookAConsult /> : null}
        <Footer />
      </div>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
        <SiteContentProvider>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/dashboard" element={<DashboardLayout />}>
                <Route index element={<DashboardPage />} />
                <Route path="pages" element={<PagesManager />} />
                <Route
                  path="pages/:pageId/sections/:sectionId"
                  element={<SectionEditor />}
                />
                <Route
                  path="pages/:pageId/sections"
                  element={<SectionsManager />}
                />
                <Route
                  path="sections/:sectionId"
                  element={<LegacySectionEditorRedirect />}
                />
                <Route path="content" element={<DynamicContentPlaceholder />} />
                <Route path="media" element={<MediaManager />} />
                <Route path="contacts" element={<ContactsManager />} />
                <Route path="services" element={<ServicesManager />} />
                <Route path="projects" element={<ProjectsManager />} />
                <Route path="blogs" element={<BlogsManager />} />
                <Route path="menus" element={<MenusManager />} />
              </Route>
            </Route>
            <Route path="/*" element={<AppLayout />} />
          </Routes>
        </Suspense>
        </SiteContentProvider>
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
